import React, { useEffect, useRef, useState } from 'react';

import { TOAST_AUTO_CLOSE_DURATION, notificationFailure, notificationSuccess, notificationWarning } from '../../theme';

export type NotificationVariant = 'success' | 'failure' | 'warning';

interface NotificationCardProps {
  variant: NotificationVariant;
  title: string;
  subtitle?: string;
  onClose?: () => void;
  autoHideDuration?: number; // in milliseconds, 0 to disable
}

const variantStyles = {
  success: {
    background: notificationSuccess.background,
    accentColor: notificationSuccess.accent,
    progressBarColor: notificationSuccess.accent,
    textColor: notificationSuccess.text,
    subtitleColor: notificationSuccess.subtitle,
    iconPath: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z', // Checkmark
  },
  failure: {
    background: notificationFailure.background,
    accentColor: notificationFailure.accent,
    progressBarColor: notificationFailure.accent,
    textColor: notificationFailure.text,
    subtitleColor: notificationFailure.subtitle,
    iconPath: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z', // Close/X
  },
  warning: {
    background: notificationWarning.background,
    accentColor: notificationWarning.accent,
    progressBarColor: notificationWarning.accent,
    textColor: notificationWarning.text,
    subtitleColor: notificationWarning.subtitle,
    iconPath: 'M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z', // Exclamation
  },
};

export const NotificationCard: React.FC<NotificationCardProps> = ({
  variant,
  title,
  subtitle,
  onClose,
  autoHideDuration = TOAST_AUTO_CLOSE_DURATION,
}) => {
  const [progress, setProgress] = useState(100);
  const isMountedRef = useRef(true);
  const styles = variantStyles[variant];

  useEffect(() => {
    // Track mounted state to prevent calling onClose after unmount
    isMountedRef.current = true;

    if (autoHideDuration <= 0) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      if (!isMountedRef.current) {
        clearInterval(interval);
        return;
      }

      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / autoHideDuration) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        // Only call onClose if component is still mounted
        if (isMountedRef.current) {
          onClose?.();
        }
      }
    }, 50);

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, [autoHideDuration, onClose]);

  return (
    <div
      className="relative flex items-stretch rounded-xl shadow-lg overflow-hidden min-w-[320px] max-w-[420px] pb-5"
      style={{ background: styles.background }}
    >
      {/* Left Icon Container */}
      <div className="flex items-center justify-center p-4">
        <div className="flex items-center justify-center w-10 h-10 bg-white rounded-lg">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill={styles.accentColor}>
            <path d={styles.iconPath} />
          </svg>
        </div>
      </div>

      {/* Vertical Divider */}
      <div className="w-0.5 my-3 mb-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)' }} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center py-3 px-4 pr-10 pb-6">
        <span className="font-semibold leading-tight" style={{ color: styles.textColor }}>
          {title}
        </span>
        {subtitle && (
          <span className="text-sm mt-1 leading-snug" style={{ color: styles.subtitleColor }}>
            {subtitle}
          </span>
        )}
      </div>

      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-2 right-2 p-1 rounded opacity-70 hover:opacity-100 hover:bg-black/10 transition-opacity"
        style={{ color: styles.textColor }}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </button>

      {/* Progress Bar - with mt-2.5 (10px) gap from content above */}
      <div
        className="absolute bottom-2 left-4 right-4 h-1.5 rounded-full mt-2.5"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.4)' }}
      >
        <div
          className="h-full rounded-full transition-[width] duration-[50ms] linear"
          style={{
            width: `${progress}%`,
            backgroundColor: styles.progressBarColor,
          }}
        />
      </div>
    </div>
  );
};
